import { Toaster, toast } from "react-hot-toast";
import { Button } from "../button/button";
import { Loading } from "../icon-helpers/loading";
import { AlertIcon } from "../icons/alert-icon";
import { CheckmarkIcon } from "../icons/checkmark-icon";

interface SuccessToast {
  id?: string;
  message: React.ReactNode;
  dismissible?: boolean;
  duration?: number;
}

interface ErrorToast {
  id?: string;
  error?: unknown;
  message: React.ReactNode;
  dismissible?: boolean;
  duration?: number;
}

interface LoadingToast {
  id?: string;
  message: React.ReactNode;
}

function createID() {
  const id = Math.floor(Math.random() * 1_000_000);
  return id;
}

const defaultDuration = (params: {
  duration?: number;
  dismissible?: boolean;
}) => {
  if (params.dismissible && params.duration === undefined) {
    return 10_000;
  }

  return params.duration;
};

export const notifySuccess = (params: SuccessToast) => {
  toast(
    (t) => (
      <div className="flex w-full items-center justify-between gap-2 bg-tertiary-background p-2 text-white">
        <div className="flex items-center gap-2">
          <CheckmarkIcon className="size-6 flex-shrink-0 text-status-green" />
          {params.message}
        </div>
        {params.dismissible && (
          <Button
            className="whitespace-nowrap border border-stroke-input bg-transparent text-white"
            onClick={() => toast.dismiss(t.id)}
          >
            Dismiss
          </Button>
        )}
      </div>
    ),
    {
      id: params.id ?? `toast-${createID()}`,
      duration: defaultDuration(params),
    },
  );
};

export const notifyError = (params: ErrorToast) => {
  toast(
    (t) => (
      <div className="flex w-full items-center justify-between gap-2 bg-tertiary-background p-2 text-white">
        <div className="flex items-center gap-2">
          <AlertIcon className="size-6 flex-shrink-0 text-status-yellow" />
          <div className="flex max-w-[500px] flex-col gap-1 break-words">
            {params.message}
            {params.error ? (
              <span>Reason: {(params.error as Error).message}</span>
            ) : null}
          </div>
        </div>
        {params.dismissible && (
          <Button
            className="whitespace-nowrap border border-stroke-input bg-transparent text-white"
            onClick={() => toast.dismiss(t.id)}
          >
            Dismiss
          </Button>
        )}
      </div>
    ),
    {
      id: params.id ?? `toast-${createID()}`,
      duration: defaultDuration(params),
    },
  );
};

export const notifyLoading = (params: LoadingToast) =>
  toast(
    () => (
      <div className="flex w-full items-center gap-2 bg-tertiary-background p-2 text-white">
        <Loading className="size-6 flex-shrink-0 text-primary-foreground" />
        {params.message}
      </div>
    ),
    {
      id: params.id ?? `toast-${createID()}`,
      duration: Number.POSITIVE_INFINITY,
    },
  );

export const clearNotifications = () => toast.dismiss();

export function Notifications() {
  return (
    <Toaster
      position="bottom-center"
      toastOptions={{
        style: {
          display: "flex",
          flexDirection: "row",
          minWidth: "300px",
          maxWidth: "752px",
          minHeight: "52px",
          columnGap: "0.5rem",
          alignItems: "center",
          fontWeight: "400",
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          boxShadow: "12px 12px 15px rgba(0, 0, 0, 0.25)",
          borderRadius: "0.5rem",
          backgroundColor: "rgb(var(--tertiary-background))",
          padding: "0px",
        },
      }}
    />
  );
}
